'use client'

import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ProBadge,
} from '@styleguide'
import { Alert, AlertTitle } from '@styleguide/components/ui/alert'
import {
  DoorOpenIcon,
  InfoIcon,
  MessageSquareIcon,
  ShieldCheckIcon,
  UsersIcon,
} from '@styleguide/components/ui/icons'
import { EVENTS, trackEvent } from 'helpers/analyticsHelper'
import {
  SMS_CUTOFF_COPY,
  formatSmsCutoffDate,
} from 'app/dashboard/campaign-verification/verificationCopy'
import { useSmsCompliance } from 'app/dashboard/shared/smsCutoff/useSmsCompliance'
import {
  proUpgradeHref,
  type ProUpgradeChannel,
  type ProUpgradeSource,
} from 'app/dashboard/pro-upgrade/proUpgradeAttribution'
import { MEMBERSHIP_COPY } from './membershipCopy'

const TILE_ICONS = [UsersIcon, MessageSquareIcon, DoorOpenIcon, ShieldCheckIcon]

// The design's `TINT` washes are brand-palette steps (midnight-100, then the
// 200 of lavender, halo green and waxflower), not the theme's `-light` roles.
const TILE_TINTS = [
  'bg-brand-midnight-100',
  'bg-brand-lavender-200',
  'bg-brand-halo-green-200',
  'bg-brand-waxflower-200',
]

interface ProPitchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // Which wall opened the pitch, for its events and the wizard's Flow Started.
  source: ProUpgradeSource
  channel: ProUpgradeChannel
}

// The membership surface's Pro pitch (design: renderProPitch): what Pro costs
// and the four things it unlocks. Closing it is the X, so the only button is
// the one that joins.
export const ProPitchDialog = ({
  open,
  onOpenChange,
  source,
  channel,
}: ProPitchDialogProps): React.JSX.Element => {
  const router = useRouter()
  const { smsCompliance, smsCutoffDate } = useSmsCompliance()
  const smsClosed = smsCompliance === 'closed'
  // Verification only exists to unlock texting, so once texting has closed
  // for the race the tile sells nothing Pro can still deliver.
  const tiles = MEMBERSHIP_COPY.pitch.tiles
    .map((tile, index) => ({
      ...tile,
      Icon: TILE_ICONS[index]!,
      tint: TILE_TINTS[index],
    }))
    .filter(({ title }) => !smsClosed || title !== 'Campaign verification')

  useEffect(() => {
    if (open) {
      trackEvent(EVENTS.ProUpgrade.Compliance.InterstitialViewed, { channel })
    }
  }, [open, channel])

  const handleJoin = () => {
    trackEvent(EVENTS.ProUpgrade.Compliance.InterstitialCompleted, { channel })
    onOpenChange(false)
    router.push(
      proUpgradeHref({
        source,
        channel,
        cta: MEMBERSHIP_COPY.pitch.join,
      }),
    )
  }

  // Every other way out of the dialog — the X, escape, the overlay — is a
  // dismissal, and there is no dismiss button left to report it.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      trackEvent(EVENTS.ProUpgrade.Compliance.InterstitialDismissed, {
        channel,
      })
    }
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* DialogContent's own `sm:max-w-lg` would cap the 640px the design
          draws, so the width is set at the same breakpoint. */}
      <DialogContent className="flex max-h-[88vh] flex-col gap-5 rounded-2xl p-5 sm:max-w-[640px] sm:gap-6 sm:p-8">
        {/* Only the body scrolls, so a short viewport never carries the Join
            button off the bottom of the dialog. */}
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto sm:gap-6">
          {/* DialogHeader's own classes end in `sm:text-left`, so centering
              the wrapped lines needs the sm: breakpoint spelled out too. */}
          <DialogHeader className="items-center gap-3 text-center sm:text-center">
            <ProBadge size="large" className="h-[30px] w-[66px]" />
            <DialogTitle className="text-xl font-semibold tracking-tight sm:text-2xl">
              {MEMBERSHIP_COPY.pitch.title}
            </DialogTitle>
            <span className="inline-flex h-8 items-center rounded-full bg-info-50 px-3.5 text-[15px] font-medium text-primary">
              {MEMBERSHIP_COPY.pitch.pill}
            </span>
          </DialogHeader>
          {smsClosed && (
            <Alert variant="info" icon={<InfoIcon />}>
              <AlertTitle>
                {SMS_CUTOFF_COPY.closedReason(
                  smsCutoffDate ? formatSmsCutoffDate(smsCutoffDate) : null,
                )}
              </AlertTitle>
            </Alert>
          )}
          <div className="grid gap-5 rounded-xl border border-brand-blue-200 p-5 sm:grid-cols-2 sm:gap-6 sm:p-6">
            {tiles.map(({ title, body, Icon, tint }) => (
              <div key={title} className="flex flex-col gap-2">
                <span
                  className={`flex size-10 items-center justify-center rounded-full ${tint}`}
                >
                  <Icon className="size-5" aria-hidden />
                </span>
                <p className="font-semibold">{title}</p>
                <p className="text-sm text-base-muted-foreground">{body}</p>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter className="shrink-0 sm:justify-center">
          <Button
            className="w-full sm:w-auto sm:min-w-[360px]"
            onClick={handleJoin}
          >
            {MEMBERSHIP_COPY.pitch.join}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
